import { CellState, cellKey, queenAttacks } from '../board';
import {
  Analysis, HintUnit, RuleId, RuleInstance, TechniqueSig,
  analyze, attacksOf, enumerateFrom, spatialAttacksOf,
} from '../rules';
import { BitSet } from './bitset';
import { FactId, applyFact, elimFact, factCount, factOfCell, queenFact } from './facts';
import { ANALYZER_VERSION } from './version';

// The deduction graph: every way a puzzle can be solved by the rule engine, in one structure.
//
// Because every rule is sound and monotone, the closure is CONFLUENT — the set of derivable
// facts does not depend on the order you apply them. That is what makes this tractable: the
// space of solve *paths* is exponential, but the space of derivable *facts* is not, so we build
// the facts once and leave ordering to whoever walks it.
//
// Shape: an AND/OR graph. Facts are OR-nodes (any one support suffices). Supports are AND-nodes
// (all premises needed). A player may apply any subset of a support's yields, which is why
// facts, not rule instances, are the unit of account.
//
// ── The subtlety that shapes everything ──
// A rule instance is NOT state-independent. An R7 instance for a region is defined by that
// region's *current* candidate list, so a player who has marked fewer Xs faces a different
// instance with a weaker conclusion. Enumerating instances across all reachable states is
// exponential. So the edge we store is not an instance but a per-fact MINIMAL SUPPORT: the
// smallest set of premises that still yields the fact. Any state containing those premises
// derives the fact, which correctly covers players who are running behind on bookkeeping, and
// turns liveness into a bitmask subset test instead of a re-enumeration.

export interface FactNode {
  id: FactId;
  kind: 'elim' | 'queen';
  r: number;
  c: number;
  /** Earliest closure round at which this becomes derivable. A LOWER BOUND on when a player
   *  could know it, not a claim about the order anyone actually solves in. */
  round: number;
  /** OR-node: any one of these yields the fact. */
  supports: number[];
  /** Queen reach — free under Auto-Xs, one click of bookkeeping under manual play. */
  bookkeeping: boolean;
  derived: boolean;
}

export interface Support {
  id: number;
  sig: TechniqueSig;
  units: HintUnit[];
  /** Live candidate cells driving the deduction — for board highlighting. */
  causeCells: string[];
  /** Every cell the underlying instance concludes about — for display and "apply all". Not all
   *  of these are justified by `premises`; each has its own support. */
  cells: string[];
  /** Minimal premise facts (AND) — exactly what this one fact needs. */
  premises: FactId[];
  premiseMask: BitSet;
  /** The single fact this support establishes. */
  yields: FactId[];
  yieldMask: BitSet;
  /** 1 + max(round of premises). */
  round: number;
}

export interface DeductionGraph {
  size: number;
  colorRegions: string[][];
  engineVersion: string;
  facts: FactNode[];
  supports: Support[];
  /** fact → ids of supports that list it as a premise. Drives incremental liveness updates. */
  premiseIndex: number[][];
  /** Number of closure rounds executed. */
  rounds: number;
  /** Did the closure place all N queens? false ⇒ the rules cannot finish this puzzle. */
  solved: boolean;
  /** Queen facts in the closure, as a per-row column array, if solved. */
  solution: number[] | null;
}

export interface BuildOptions {
  /** Rules to withhold — used to compute which techniques a puzzle strictly requires. */
  disable?: ReadonlySet<RuleId>;
  /** Keep recording extra supports for a fact this many rounds after it is first derived.
   *  These are what make "how many different ways could a player get here?" answerable. */
  supportHorizon?: number;
  /** Skip premise minimization (faster, but over-serializes — for throwaway measurements). */
  minimizePremises?: boolean;
}

function emptyBoard(size: number): CellState[][] {
  return Array.from({ length: size }, () => Array<CellState>(size).fill(null));
}

/** The cells belonging to a row / column / region. */
function unitCells(unit: HintUnit, size: number, colorRegions: string[][]): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  if (unit.type === 'row') { for (let c = 0; c < size; c++) out.push([unit.index, c]); return out; }
  if (unit.type === 'col') { for (let r = 0; r < size; r++) out.push([r, unit.index]); return out; }
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++)
    if (colorRegions[r][c] === unit.color) out.push([r, c]);
  return out;
}

const lineIndexOf = (unit: HintUnit, r: number, c: number): number =>
  unit.type === 'row' ? r : c;

/** The shape both RuleInstance and Support satisfy — enough to re-check a deduction. */
export interface Deduction {
  sig: TechniqueSig;
  units: HintUnit[];
  causeCells: string[];
}

/** Does every legal placement of this pair of units attack the target? (R9's core predicate.) */
function pairCovers(
  A: Analysis, C1: Array<[number, number]>, C2: Array<[number, number]>, targetKey: string,
): boolean {
  let anyLegal = false;
  for (const [r1, c1] of C1) {
    const a1 = attacksOf(A, r1, c1);
    for (const [r2, c2] of C2) {
      const same = r1 === r2 && c1 === c2;
      if (!same && a1.has(cellKey(r2, c2))) continue; // the pair would attack each other
      anyLegal = true;
      const covered = same ? a1.has(targetKey) : a1.has(targetKey) || attacksOf(A, r2, c2).has(targetKey);
      if (!covered) return false;
    }
  }
  return anyLegal;
}

/**
 * Does this exact deduction still hold on the given board state, and still yield `targetKey`?
 *
 * Re-checked directly per rule family rather than by re-running the enumerator, for two
 * reasons. It is much faster (no whole-board walk), and — more importantly — it is immune to
 * *classification* drift: relaxing a board can turn an R3 into an R7, or let some unrelated
 * rule reach the same cell, neither of which tells you anything about whether *this* support's
 * premises are doing work. This is the predicate premise minimality is defined against.
 */
export function deductionHolds(A: Analysis, d: Deduction, targetKey: string): boolean {
  const size = A.size;
  const regions = A.colorRegions;
  const [tr, tc] = targetKey.split(',').map(Number);
  const isCand = (r: number, c: number) => A.candKeys.has(cellKey(r, c));
  const candOf = (u: HintUnit) => unitCells(u, size, regions).filter(([r, c]) => isCand(r, c));
  const colorsOf = (keys: string[]) => new Set(keys.map(k => {
    const [r, c] = k.split(',').map(Number);
    return regions[r][c];
  }));

  switch (d.sig.rule) {
    case 'R2': case 'R3': case 'R7': {
      if (!isCand(tr, tc)) return false;
      const u = d.sig.rule === 'R2' ? d.units[0] : (d.units.find(x => x.type === 'region') ?? d.units[0]);
      const atk = d.sig.rule === 'R2' ? spatialAttacksOf : attacksOf;
      const C = candOf(u);
      if (C.length < 2) return false;
      return C.every(([r, c]) => atk(A, r, c).has(targetKey));
    }
    case 'R8': {
      const C = candOf(d.units[0]);
      return C.length === 1 && C[0][0] === tr && C[0][1] === tc;
    }
    case 'R4': {
      if (!isCand(tr, tc)) return false;
      const line = d.units[0];
      const reg = d.units.find(u => u.type === 'region');
      const color = reg && reg.type === 'region' ? reg.color : null;
      const C = candOf(line);
      if (C.length < 2 || !C.every(([r, c]) => regions[r][c] === color)) return false;
      const inLine = lineContains(line, tr, tc);
      return regions[tr][tc] === color && !inLine;
    }
    case 'R5': {
      if (!isCand(tr, tc)) return false;
      const S = colorsOf(d.causeCells);
      const L = d.units;
      if (S.size !== L.length) return false;
      // every candidate of every color in S must lie inside the lines
      for (const color of S) {
        for (const [r, c] of A.candByColor.get(color) ?? []) {
          if (!L.some(u => lineContains(u, r, c))) return false;
        }
      }
      // target sits in one of those lines and belongs to some other color
      return L.some(u => lineContains(u, tr, tc)) && !S.has(regions[tr][tc]);
    }
    case 'R6': {
      if (!isCand(tr, tc)) return false;
      const S = colorsOf(d.causeCells);
      const L = d.units;
      if (S.size !== L.length) return false;
      // the lines must span no color outside S
      for (const u of L) for (const [r, c] of candOf(u)) if (!S.has(regions[r][c])) return false;
      // target belongs to one of those colors but lies outside the lines
      return S.has(regions[tr][tc]) && !L.some(u => lineContains(u, tr, tc));
    }
    case 'R9': {
      if (!isCand(tr, tc)) return false;
      return pairCovers(A, candOf(d.units[0]), candOf(d.units[1]), targetKey);
    }
  }
  return false;
}

function lineContains(u: HintUnit, r: number, c: number): boolean {
  return u.type === 'row' ? u.index === r : u.type === 'col' ? u.index === c : false;
}

/**
 * The minimal set of already-known facts a support needs.
 *
 * Computed exactly and in closed form per rule family rather than by trial re-enumeration —
 * for the shadow rules the answer is a one-line characterization, and re-running the engine
 * once per premise per fact per round would cost days over the corpus.
 *
 * For a shadow rule ("target t is attacked by every candidate of unit U"), restoring an
 * eliminated cell k of U as a candidate breaks the deduction *iff k does not itself attack t*.
 * So the required premises are exactly U's eliminated cells that do not attack t — and since
 * every cell we leave out does attack t, leaving them all out simultaneously is still sound.
 * The other families have equally direct characterizations, noted inline.
 */
function minimalPremises(
  A: Analysis, board: CellState[][], inst: RuleInstance, target: FactId, targetKey: string,
): FactId[] {
  const size = A.size;
  const regions = A.colorRegions;
  const out: FactId[] = [];
  const push = (r: number, c: number) => {
    const f = factOfCell(board, r, c);
    if (f !== null) out.push(f);
  };
  const marked = (r: number, c: number) => board[r][c] !== null;

  switch (inst.sig.rule) {
    // ── Shadow over one unit's candidates ──
    case 'R2': case 'R3': case 'R7': {
      // R2 reasons spatially over a line; R3/R7 reason over a color region (R3's units are
      // [region, line] — the region is the one whose candidates form the shadow).
      const unit = inst.sig.rule === 'R2' ? inst.units[0]
        : (inst.units.find(u => u.type === 'region') ?? inst.units[0]);
      const atk = inst.sig.rule === 'R2' ? spatialAttacksOf : attacksOf;
      for (const [r, c] of unitCells(unit, size, regions)) {
        if (!marked(r, c)) continue;
        if (!atk(A, r, c).has(targetKey)) push(r, c); // restoring this would break the shadow
      }
      break;
    }

    // ── A unit with a single candidate ──
    // Every other cell of the unit must be eliminated; restoring any one gives a second
    // candidate and the placement is no longer forced. So all of them are required.
    case 'R8': {
      for (const [r, c] of unitCells(inst.units[0], size, regions)) {
        if (marked(r, c) && !(elimFact(r, c, size) === target || queenFact(r, c, size) === target)) push(r, c);
      }
      break;
    }

    // ── A line whose only candidates are one color ──
    // Restoring a same-color cell keeps the line single-colored, so only the *other*-colored
    // eliminated cells of the line are load-bearing.
    case 'R4': {
      const line = inst.units[0];
      const region = inst.units.find(u => u.type === 'region');
      const color = region && region.type === 'region' ? region.color : null;
      for (const [r, c] of unitCells(line, size, regions)) {
        if (marked(r, c) && regions[r][c] !== color) push(r, c);
      }
      break;
    }

    // ── Pigeonhole: n colors confined to n lines ──
    // What keeps them confined is that they have no candidate outside those lines.
    case 'R5': {
      const colors = new Set(inst.causeCells.map(k => {
        const [r, c] = k.split(',').map(Number);
        return regions[r][c];
      }));
      const lines = new Set(inst.units.map(u => (u.type === 'row' || u.type === 'col' ? u.index : -1)));
      const axisIsRow = inst.units[0]?.type === 'row';
      for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) {
        if (!marked(r, c) || !colors.has(regions[r][c])) continue;
        if (!lines.has(axisIsRow ? r : c)) push(r, c);
      }
      break;
    }

    // ── Pigeonhole dual: n lines spanning only n colors ──
    // What keeps the span small is that no *other* color has a candidate in those lines.
    case 'R6': {
      const colors = new Set(inst.causeCells.map(k => {
        const [r, c] = k.split(',').map(Number);
        return regions[r][c];
      }));
      const axisIsRow = inst.units[0]?.type === 'row';
      for (const u of inst.units) {
        for (const [r, c] of unitCells(u, size, regions)) {
          if (marked(r, c) && !colors.has(regions[r][c])) push(r, c);
        }
      }
      void axisIsRow;
      break;
    }

    // ── Pairwise joint shadow ──
    // The only family without a safe closed form. Restoring cell k of one unit breaks the
    // deduction iff k can pair legally with some candidate of the other unit such that neither
    // attacks the target — but individually-necessary cells are not always *jointly* sufficient,
    // because restoring cells in BOTH units simultaneously can create a legal pair that neither
    // restoration alone would. So: try the individually-necessary set, and fall back to honest
    // greedy minimization when it does not hold.
    case 'R9': {
      const [u1, u2] = inst.units;
      const cand = (u: HintUnit) => unitCells(u, size, regions).filter(([r, c]) => !marked(r, c));
      const C1 = cand(u1), C2 = cand(u2);
      const breaksWith = (r: number, c: number, others: Array<[number, number]>) => {
        const ak = attacksOf(A, r, c);
        if (ak.has(targetKey)) return false; // k itself covers the target, so it is harmless
        for (const [pr, pc] of others) {
          const same = pr === r && pc === c;
          if (!same && ak.has(cellKey(pr, pc))) continue; // illegal pair
          if (!attacksOf(A, pr, pc).has(targetKey)) return true;
        }
        return false;
      };
      const marks = (u: HintUnit) => unitCells(u, size, regions).filter(([r, c]) => marked(r, c));
      const necessary = [
        ...marks(u1).filter(([r, c]) => breaksWith(r, c, C2)),
        ...marks(u2).filter(([r, c]) => breaksWith(r, c, C1)),
      ];
      const holds = (cells: Array<[number, number]>) =>
        deductionHolds(analyzeReusing(boardFrom(size, cells), regions, A), inst, targetKey);

      let chosen = necessary;
      if (!holds(necessary)) {
        // Greedy: start from everything the two units already know and drop what is not needed.
        // Fixed row-major order so graphs stay reproducible. Greedy gives a *minimal*
        // (irredundant) set, not necessarily a *minimum* one — that distinction is fine here.
        chosen = [...marks(u1), ...marks(u2)];
        for (const k of [...chosen]) {
          const trial = chosen.filter(x => x !== k);
          if (holds(trial)) chosen = trial;
        }
      }
      for (const [r, c] of chosen) push(r, c);
      break;
    }
  }

  return [...new Set(out)];
}

/** A board with exactly these cells marked X. Used for premise trials. */
function boardFrom(size: number, cells: Array<[number, number]>): CellState[][] {
  const b = emptyBoard(size);
  for (const [r, c] of cells) b[r][c] = 'X';
  return b;
}

/** Analyze a hypothetical board, sharing the parent's attack caches (they are board-independent). */
function analyzeReusing(board: CellState[][], colorRegions: string[][], parent: Analysis): Analysis {
  const A2 = analyze(board, colorRegions);
  A2.attackCache = parent.attackCache;
  A2.spatialCache = parent.spatialCache;
  return A2;
}

/** Raw (non-minimal but always sufficient) premises. */
function rawPremises(A: Analysis, board: CellState[][], inst: RuleInstance): FactId[] {
  const out = new Set<FactId>();
  const add = (r: number, c: number) => {
    const f = factOfCell(board, r, c);
    if (f !== null) out.add(f);
  };
  if (inst.sig.rule === 'R5') {
    // R5's premises live OUTSIDE its units: what confines the colors to those lines is that
    // they have no candidate anywhere else. Walking the units would miss all of it.
    const colors = new Set(inst.causeCells.map(k => {
      const [r, c] = k.split(',').map(Number);
      return A.colorRegions[r][c];
    }));
    for (let r = 0; r < A.size; r++) for (let c = 0; c < A.size; c++) {
      if (colors.has(A.colorRegions[r][c])) add(r, c);
    }
    return [...out];
  }
  for (const u of inst.units) {
    for (const [r, c] of unitCells(u, A.size, A.colorRegions)) add(r, c);
  }
  return [...out];
}

/**
 * Build the full deduction graph by running the rule closure from an empty board.
 *
 * Canonical semantics are Auto-Xs-on: a placed queen's reach is folded in for free and those
 * facts are flagged `bookkeeping`, so difficulty can be measured on reasoning alone while a
 * manual-mode simulation can still charge for writing them down.
 */
export function buildGraph(colorRegions: string[][], opts?: BuildOptions): DeductionGraph {
  const size = colorRegions.length;
  const horizon = opts?.supportHorizon ?? 2;
  const minimize = opts?.minimizePremises ?? true;
  const nFacts = factCount(size);

  const facts: FactNode[] = Array.from({ length: nFacts }, (_, id) => ({
    id,
    kind: id >= size * size ? 'queen' : 'elim',
    r: Math.floor((id % (size * size)) / size),
    c: (id % (size * size)) % size,
    round: Infinity,
    supports: [],
    bookkeeping: false,
    derived: false,
  }));

  const supports: Support[] = [];
  const premiseIndex: number[][] = Array.from({ length: nFacts }, () => []);
  const seen = new Set<string>();
  const board = emptyBoard(size);

  const derive = (f: FactId, round: number, bookkeeping: boolean) => {
    const node = facts[f];
    if (!node.derived) {
      node.derived = true;
      node.bookkeeping = bookkeeping;
      applyFact(board, f);
    }
    if (round < node.round) node.round = round;
  };

  // Fold in everything a placed queen already says. Free under Auto-Xs; the simulator charges
  // motor time for these in manual mode.
  const closeBookkeeping = (round: number) => {
    for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) {
      if (board[r][c] !== 'queen') continue;
      for (const k of queenAttacks(r, c, colorRegions)) {
        const [ar, ac] = k.split(',').map(Number);
        if (board[ar][ac] === null) derive(elimFact(ar, ac, size), round, true);
      }
    }
  };

  let round = 0;
  for (; round <= size * size; round++) {
    closeBookkeeping(round);

    const A = analyze(board, colorRegions);
    const instances = enumerateFrom(A, { disable: opts?.disable });
    const newFacts: FactId[] = [];

    for (const inst of instances) {
      for (const cellK of inst.cells) {
        const [r, c] = cellK.split(',').map(Number);
        const target = inst.kind === 'place' ? queenFact(r, c, size) : elimFact(r, c, size);
        const node = facts[target];
        // Keep collecting alternate supports for a little while after a fact first lands —
        // that is what makes "how else could this have been reached?" answerable — but not
        // forever, or the graph fills with restatements.
        if (node.derived && node.round + horizon < round) continue;

        const premises = minimize
          ? minimalPremises(A, board, inst, target, cellK)
          : rawPremises(A, board, inst);

        let sRound = 1;
        for (const p of premises) sRound = Math.max(sRound, facts[p].round + 1);

        const premiseMask = BitSet.of(nFacts, premises);
        // One support per (instance, target). `premises` is minimal for THIS fact and justifies
        // only this fact, so `yields` must be exactly it — an instance's other conclusions may
        // well need different premises. The full instance conclusion is kept in `cells` so the
        // UI and the simulator can still offer "apply the whole deduction", taking each cell
        // whose own support is live.
        const yieldFacts = [target];
        const dedupeKey = `${inst.sig.rule}/${inst.sig.variant}/${inst.sig.n}|${[...premises].sort((a, b) => a - b).join(',')}|${target}`;

        if (!seen.has(dedupeKey)) {
          seen.add(dedupeKey);
          const s: Support = {
            id: supports.length,
            sig: inst.sig,
            units: inst.units,
            causeCells: inst.causeCells,
            cells: inst.cells,
            premises,
            premiseMask,
            yields: yieldFacts,
            yieldMask: BitSet.of(nFacts, yieldFacts),
            round: sRound,
          };
          supports.push(s);
          for (const p of premises) premiseIndex[p].push(s.id);
          node.supports.push(s.id);
          if (sRound < node.round) node.round = sRound;
        }

        if (!node.derived) newFacts.push(target);
      }
    }

    if (newFacts.length === 0) break;
    for (const f of newFacts) derive(f, facts[f].round === Infinity ? round + 1 : facts[f].round, false);
  }
  closeBookkeeping(round);

  // Read the solution off the closure.
  const solution: number[] = Array(size).fill(-1);
  let queens = 0;
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) {
    if (board[r][c] === 'queen') { solution[r] = c; queens++; }
  }
  const solved = queens === size && solution.every(c => c >= 0);

  return {
    size, colorRegions, engineVersion: ANALYZER_VERSION,
    facts, supports, premiseIndex, rounds: round,
    solved, solution: solved ? solution : null,
  };
}

/** Facts the closure established, in round order — the canonical "solution path" spine. */
export function derivedFacts(g: DeductionGraph): FactNode[] {
  return g.facts.filter(f => f.derived).sort((a, b) => a.round - b.round || a.id - b.id);
}
